import { useState } from 'react';
import { siteConfig } from '@/mocks/site';
import SEOMeta from '@/components/feature/SEOMeta';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = (formData.get('website_alt') as string || '').trim();
    if (honeypot) {
      setFormStatus('success');
      form.reset();
      return;
    }

    try {
      const response = await fetch('https://readdy.ai/api/form/d9pj5gmb4qjhggaepvbg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });

      const responseText = await response.text();
      let parsed;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        setFormError('');
        form.reset();
      } else if (serverMsg.toLowerCase().includes('spam')) {
        setFormStatus('error');
        setFormError('提交失败，请稍后再试。');
      } else {
        setFormStatus('error');
        setFormError(typeof serverMsg === 'string' ? serverMsg : '提交失败，请稍后再试。');
      }
    } catch {
      setFormStatus('error');
      setFormError('网络错误，请检查连接后重试。');
    }
  };

  const pageTitle = '关于我们 - 91大事件';
  const pageDesc = '了解91大事件的品牌故事、使命愿景和团队价值观。91大事件致力于为读者提供系统、全面、深入的历史事件解读。';
  const pageKeywords = '91大事件,关于91大事件,91大事件品牌介绍,历史知识平台,关于我们';

  const orgJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: siteConfig.name,
    url: siteConfig.url,
    description: siteConfig.about.subtitle,
    email: siteConfig.about.contact.email,
    address: {
      '@type': 'PostalAddress',
      addressLocality: '北京市',
      addressRegion: '海淀区',
      streetAddress: siteConfig.about.contact.address,
      addressCountry: 'CN',
    },
  };

  const pageJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: pageTitle,
    description: pageDesc,
    url: `${siteConfig.url}/about`,
    about: {
      '@type': 'Organization',
      name: siteConfig.name,
    },
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(orgJsonLd)}</script>
      <script type="application/ld+json">{JSON.stringify(pageJsonLd)}</script>
      <SEOMeta
        title={pageTitle}
        description={pageDesc}
        keywords={pageKeywords}
        canonicalUrl={`${siteConfig.url}/about`}
      />
      <Navbar />
      <main className="pt-16">
        <section className="py-10 md:py-14 bg-primary-500">
          <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
            <div className="mb-6">
              <Breadcrumb
                items={[
                  { label: '首页', path: '/' },
                  { label: '关于我们' },
                ]}
              />
            </div>
            <h1 className="text-2xl md:text-3xl font-heading text-background-50 mb-2">
              {siteConfig.about.title}
            </h1>
            <p className="text-sm text-background-50/80">{siteConfig.about.subtitle}</p>
          </div>
        </section>

        <section className="py-10 md:py-14 bg-background-50">
          <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-start">
              <div>
                <h2 className="text-xl font-heading text-foreground-950 mb-4">品牌故事</h2>
                <div
                  className="text-sm text-foreground-600 leading-relaxed space-y-3"
                  dangerouslySetInnerHTML={{ __html: siteConfig.about.story }}
                />
              </div>
              <div className="bg-background-100 rounded-lg p-6 md:p-8">
                <h3 className="text-lg font-heading text-foreground-950 mb-3">我们的使命</h3>
                <p className="text-sm text-foreground-600 mb-6 leading-relaxed">
                  {siteConfig.about.mission}
                </p>
                <h3 className="text-lg font-heading text-foreground-950 mb-3">我们的愿景</h3>
                <p className="text-sm text-foreground-600 leading-relaxed">
                  {siteConfig.about.vision}
                </p>
              </div>
            </div>
          </div>
        </section>

        <section className="py-10 md:py-14 bg-background-100">
          <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
            <h2 className="text-xl font-heading text-foreground-950 mb-8 text-center">核心价值观</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {siteConfig.about.values.map((value) => (
                <div key={value.title} className="bg-background-50 rounded-lg p-5">
                  <h3 className="text-sm font-semibold text-foreground-950 mb-2">{value.title}</h3>
                  <p className="text-xs text-foreground-600 leading-relaxed">{value.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-10 md:py-14 bg-background-50">
          <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
            <h2 className="text-xl font-heading text-foreground-950 mb-8 text-center">口碑数据</h2>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
              {siteConfig.about.reputation.map((rep) => (
                <div key={rep.label} className="text-center bg-background-100 rounded-lg p-6">
                  <div className="text-2xl md:text-3xl font-heading text-primary-500 mb-1">{rep.number}</div>
                  <div className="text-sm text-foreground-600">{rep.label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-10 md:py-14 bg-background-100">
          <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
              <div>
                <h2 className="text-xl font-heading text-foreground-950 mb-4">联系我们</h2>
                <p className="text-sm text-foreground-600 mb-6 leading-relaxed">
                  如果你有任何问题、建议或合作意向，欢迎通过以下方式联系我们，也可以填写右侧表单，我们会尽快回复。
                </p>
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 bg-primary-100 rounded-lg flex items-center justify-center">
                      <i className="ri-mail-line text-primary-600"></i>
                    </div>
                    <span className="text-sm text-foreground-700">{siteConfig.about.contact.email}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 bg-primary-100 rounded-lg flex items-center justify-center">
                      <i className="ri-map-pin-line text-primary-600"></i>
                    </div>
                    <span className="text-sm text-foreground-700">{siteConfig.about.contact.address}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 bg-primary-100 rounded-lg flex items-center justify-center">
                      <i className="ri-wechat-line text-primary-600"></i>
                    </div>
                    <span className="text-sm text-foreground-700">微信公众号：{siteConfig.about.contact.wechat}</span>
                  </div>
                </div>
              </div>

              <div className="bg-background-50 rounded-lg p-6 md:p-8">
                <h3 className="text-lg font-heading text-foreground-950 mb-4">发送消息</h3>
                {formStatus === 'success' ? (
                  <div className="text-center py-8">
                    <div className="w-12 h-12 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-3">
                      <i className="ri-check-line text-primary-600 text-xl"></i>
                    </div>
                    <p className="text-sm text-foreground-700">消息已发送，我们会尽快回复！</p>
                  </div>
                ) : (
                  <form
                    onSubmit={handleSubmit}
                    data-readdy-form
                    className="space-y-4"
                  >
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs text-foreground-600 mb-1.5">姓名</label>
                        <input
                          type="text"
                          name="name"
                          required
                          className="w-full px-3 py-2 text-sm border border-background-200 rounded-md bg-background-50 text-foreground-950 focus:outline-none focus:border-primary-400 transition-colors"
                          placeholder="请输入姓名"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-foreground-600 mb-1.5">邮箱</label>
                        <input
                          type="email"
                          name="email"
                          required
                          className="w-full px-3 py-2 text-sm border border-background-200 rounded-md bg-background-50 text-foreground-950 focus:outline-none focus:border-primary-400 transition-colors"
                          placeholder="请输入邮箱"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs text-foreground-600 mb-1.5">主题</label>
                      <input
                        type="text"
                        name="subject"
                        required
                        className="w-full px-3 py-2 text-sm border border-background-200 rounded-md bg-background-50 text-foreground-950 focus:outline-none focus:border-primary-400 transition-colors"
                        placeholder="请输入主题"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-foreground-600 mb-1.5">消息</label>
                      <textarea
                        name="message"
                        required
                        maxLength={500}
                        rows={4}
                        className="w-full px-3 py-2 text-sm border border-background-200 rounded-md bg-background-50 text-foreground-950 focus:outline-none focus:border-primary-400 transition-colors resize-none"
                        placeholder="请输入消息内容（最多500字）"
                      ></textarea>
                    </div>
                    <div className="contact-honeypot-wrapper">
                      <input
                        type="text"
                        name="website_alt"
                        tabIndex={-1}
                        autoComplete="off"
                        aria-hidden="true"
                        readOnly
                        className="contact-honeypot-field"
                      />
                    </div>
                    {formStatus === 'error' && formError && (
                      <p className="text-xs text-red-500">{formError}</p>
                    )}
                    <button
                      type="submit"
                      className="w-full px-6 py-2.5 bg-primary-500 text-background-50 rounded-md text-sm font-medium whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer"
                    >
                      发送消息
                    </button>
                  </form>
                )}
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}